import {
    IShareBaseSocket,
    SocketEmitCallback,
    SocketEventCallback,
    SocketEventUnsubscribe,
    SocketSubject
} from './socket-types';

/**
 * A wrapper around socket.io socket objects that takes care of calling
 * angular.js's rootScope.$apply when the socket emits events.
 */
export class ShareBaseSocket implements IShareBaseSocket {

    // Indicates if the socket connection is authenticated.
    private authenticated: boolean;

    /**
     * A unique identifier for a socket.
     */
    public get id(): string {
        return this.socket.id;
    }

    /**
     * Bool flag that determines if a socket is connected to the server.
     */
    public get connected(): boolean {
        return this.socket.connected;
    }

    /**
     * Bool flag that determins if a socket is not connected to the server.
     */
    public get disconnected(): boolean {
        return this.socket.disconnected;
    }

    constructor(
        private readonly $rootScope: ng.IRootScopeService,
        private readonly socket: SocketIOClient.Socket,
        private readonly subject: SocketSubject,
        private readonly authToken: string
    ) {
        this.authenticated = false;
        this.authenticate();
    }

    /**
     * Register a new handler to a socket event.
     *
     * @param eventName The name of the event to handle
     * @param cb The function to execute when the event occurs.
     * @returns A function that can be used to unsubscribe from the event
     */
    public on<TPayload>(eventName: string, cb: SocketEventCallback<TPayload>): SocketEventUnsubscribe {
        let handler = (...args: any[]) => {
            this.$rootScope.$apply(() => {
                cb.call(this.socket, ...args);
            });
        }

        this.socket.on(eventName, handler);

        return () => this.socket.removeEventListener(eventName, handler);
    }

    /**
     * Emit a new event with an optional payload that will be sent to and handled by the server.
     * 
     * @param eventName Name of the event to emit
     * @param payload The message payload to be sent
     * @param cb An optional callback that will execute once the message has been sent to the server.
     */
    public emit<TPayload>(eventName: string, payload?: TPayload, cb?: SocketEmitCallback): void {
        this.socket.emit(eventName, payload, (...args: any[]) => {
            this.$rootScope.$apply(() => {
                if (cb) {
                    cb.call(this.socket, ...args);
                }
            })
        });
    }

    /**
     * Join a room to receive start receiving events related to the room. In ShareBase rooms will be matched
     * to things such as folder views and document views. When joining a document view for a specific document,
     * the connection will receive all events related to that document.
     * 
     * @param room The room to join
     * @param roomCountCallback A function that will be executed each time another user joins or leaves the room
     */
    public joinRoom(room: string, roomCountCallback?: (users: SocketSubject[]) => void) {
        // If user is already authenticated, then just attempt to join room
        // otherwise, wait until authentication before joining the room
        this.joinRoomHelper(room, roomCountCallback);
        this.on('reconnect', () => this.joinRoomHelper(room, roomCountCallback));
    }

    /**
     * Close the socket connection by disconnecting from the server.
     */
    public disconnect(): void {
        this.socket.disconnect();
    }

    /**
     * Adds the authentication protocol to the underlying socket.
     */
    private authenticate(): void {
        // @TODO: Handle authentication errors? SBAPI-1323

        this.on('authenticated', () => {
            this.authenticated = true;
        });

        this.on('require-auth', () => {
            this.authenticated = false;
            this.emit('authenticate', {
                user: this.subject,
                token: this.authToken
            });
        });
    }

    private joinRoomHelper(room: string, roomCountCallback?: (users: SocketSubject[]) => void) {
        if (roomCountCallback && !this.socket.hasListeners('update-room-count')) {
            this.on('update-room-count', (payload: any) => {
                roomCountCallback(payload.users);
            });
        }

        if (this.authenticated) {
            this.emit('join-room', { room });
        } else {
            this.on('authenticated', () => this.emit('join-room', { room }));
        }
    }
}


// WEBPACK FOOTER //
// ./src/real-time/sockets/sharebase-socket.ts